import type { ComponentChildren, JSX } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';

/**
 * **Une couche ancrée sur son bouton** — la forme, extraite de son premier usage.
 *
 * `ObjectMenu` l'avait écrite d'abord, et pour de bonnes raisons qui n'ont rien
 * de spécifiques à un objet : une couche `absolute` dans une tuile de grille sort
 * de sa colonne — `.view` rogne en x — et se retrouve **sous le rail**, visible et
 * non cliquable. La couche est donc `fixed` et positionnée depuis la boîte du
 * bouton, ce qui oblige à borner les quatre côtés : une couche fixe ouverte sous
 * le pli ne se ramène pas à l'écran, puisque le défilement ne la déplace pas.
 *
 * Trois sorties, parce que ce sont les trois que tout le monde essaie : le clic
 * dehors, `Échap`, et le défilement — une couche qui ne suit pas son bouton doit
 * partir quand ce dernier bouge, sans quoi le clic suivant porte sur autre chose.
 *
 * Le contenu est **monté seulement quand la couche est ouverte** : un menu qui
 * s'ouvre sur une arborescence de deux cents dossiers ne doit pas les monter à
 * chaque rendu de la barre d'outils.
 */
export function Popover({
  testId,
  label,
  title,
  width,
  align = 'start',
  className,
  buttonClass = 'btn quiet',
  trigger,
  children,
}: {
  readonly testId: string;
  /** Le nom accessible du bouton. */
  readonly label: string;
  readonly title?: string;
  /** La largeur de la couche, en pixels — elle sert aussi à la borner à l'écran. */
  readonly width: number;
  /** Le bord du bouton sur lequel la couche s'aligne. */
  readonly align?: 'start' | 'end';
  readonly className?: string;
  /**
   * La classe du bouton. Le défaut est celle d'une action discrète ; le
   * commutateur d'axe passe la sienne, parce que sa couche s'ouvre depuis un
   * **segment** et qu'un segment déguisé en bouton perdrait l'état « vous êtes
   * ici » que le segment existe pour porter.
   */
  readonly buttonClass?: string;
  /** Ce que le bouton montre. Reçoit l'état d'ouverture, pour un chevron. */
  readonly trigger: (open: boolean) => ComponentChildren;
  /** Le contenu de la couche. `close` referme depuis une entrée choisie. */
  readonly children: (close: () => void) => ComponentChildren;
}): JSX.Element {
  const [at, setAt] = useState<{ top: number; left: number; maxHeight: number } | undefined>(
    undefined,
  );
  const host = useRef<HTMLSpanElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const open = at !== undefined;
  const close = (): void => setAt(undefined);

  useEffect(() => {
    if (!open) return;
    const onDown = (ev: MouseEvent): void => {
      if (!host.current?.contains(ev.target as Node)) close();
    };
    const onKey = (ev: KeyboardEvent): void => {
      if (ev.key === 'Escape') close();
    };
    // Le défilement **de la page** ferme ; celui de la couche elle-même ne doit
    // pas — d'où le test d'appartenance, absent du premier menu parce qu'il n'y
    // défilait rien.
    const onScroll = (ev: Event): void => {
      if (!host.current?.contains(ev.target as Node)) close();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  const toggle = (): void => {
    if (open) return close();
    const rect = button.current?.getBoundingClientRect();
    if (!rect) return;
    const margin = 8;
    const anchored = align === 'end' ? rect.right - width : rect.left;
    const left = Math.min(Math.max(margin, anchored), window.innerWidth - width - margin);
    const below = rect.bottom + 4;
    // La hauteur n'est pas devinée : la couche est bornée par la place réelle
    // sous — ou au-dessus — du bouton, et défile à l'intérieur. Une arborescence
    // ne se laisse pas mesurer d'avance.
    const under = window.innerHeight - below - margin;
    const over = rect.top - margin - 4;
    const flip = under < 200 && over > under;
    return setAt({
      top: flip ? Math.max(margin, rect.top - Math.min(over, 420) - 4) : below,
      left,
      maxHeight: Math.max(160, Math.min(flip ? over : under, 420)),
    });
  };

  return (
    <span class={className ? `popover-host ${className}` : 'popover-host'} ref={host}>
      <button
        type="button"
        class={`${buttonClass} popover-trigger`}
        ref={button}
        data-testid={testId}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        title={title ?? label}
        onClick={toggle}
      >
        {trigger(open)}
      </button>

      {open ? (
        <div
          class="popover"
          role="menu"
          data-testid={`${testId}-open`}
          style={{ top: `${at.top}px`, left: `${at.left}px`, width: `${width}px`, maxHeight: `${at.maxHeight}px` }}
        >
          {children(close)}
        </div>
      ) : null}
    </span>
  );
}
